import { useRouter } from 'next/router';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import dynamic from 'next/dynamic';

import { getStrapiMedia } from '../../../utils/media';
import {
  ButtonText,
  ButtonWrapper,
  InfoWrapper,
  Key,
  SelectStyled,
  StyledButton,
  StyledContentWrapper,
  StyledSectionTitle,
  TechnicalAssets,
  TechnicalDataList,
  TechnicalImage,
  TechnicalImages,
  TitleWrapper,
} from './ProductDetailsFls.style';
import { SelectType } from '../../../models/enum';
import { internationalizationToLanguages } from 'corporate-utils';
import { defaultInternationalization, defaultLanguage } from 'corporate-types';
import { DEFAULT_IMAGE } from '../../../models/domain/const';
import ImageWithFallback from '../../../components/image-with-fallback/ImageWithFallback';
import { ProductDetailsProps } from '../product-details/ProductDetails';
import { UserContext } from '../../../models/interface';

const DownloadManualModal = dynamic(
  () => import('../../../components/download-manual-modal/DownloadManualModal')
);

const ProductDetailsFls: React.FC<{
  data: ProductDetailsProps | any;
  userContext: UserContext;
  isPrerenderRequest: boolean;
}> = ({
  data,
  userContext,
  isPrerenderRequest,
}: {
  data: ProductDetailsProps;
  userContext: UserContext;
  isPrerenderRequest: boolean;
}) => {
  const { sku, prodTechnicalData, prodTechnicalImg, translations } = data ?? {};
  const { locale } = useRouter();
  const [currentModel, setCurrentModel] = React.useState<string>(
    Object.keys(prodTechnicalData)[0]
  );
  const paddingTop = useRef<any>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDownloadAvailable, setIsDownloadAvailable] = useState<
    boolean | undefined
  >(undefined);
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  const checkManualExistence = useCallback(async () => {
    const manualResponse = await fetch(
      `${process.env.NEXT_PUBLIC_URL}/api/check-modal?sku=${sku}&lang=${(
        internationalizationToLanguages(
          locale || defaultInternationalization,
          'packages/corporate-ui/src/sections/products/product-details/ProductDetails.tsx'
        ) || defaultLanguage
      )?.toUpperCase()}`
    );

    const manual = await manualResponse.json();

    setIsDownloadAvailable(manual || false);
  }, [locale, sku]);

  useEffect(() => {
    setCurrentModel(Object.keys(prodTechnicalData)[0]);
  }, [prodTechnicalData]);

  useEffect(() => {
    checkManualExistence();
  }, [checkManualExistence]);

  return Object.keys(prodTechnicalData).includes(currentModel) &&
    Object.keys(prodTechnicalData).find((el: string) => {
      return !!prodTechnicalData[el][0].value;
    }) ? (
      <StyledContentWrapper>
        <InfoWrapper>
          <TitleWrapper>
            <StyledSectionTitle>
              {translations?.pages?.productDetails?.productDetailsTitle}
            </StyledSectionTitle>
            <SelectStyled
              onChange={(val: string | number) => setCurrentModel(val as string)}
              currentValue={currentModel}
              options={Object.keys(prodTechnicalData)?.map((el: string) => ({
                value: el,
                label: prodTechnicalData[el][0].value as string,
              }))}
              type={SelectType.secondary}
            />
          </TitleWrapper>
          <TechnicalDataList>
            {Object.keys(prodTechnicalData).includes(currentModel) &&
            prodTechnicalData[currentModel]?.map(
              (technicalData, index) =>
                technicalData.name &&
                technicalData.value && (
                  <tr>
                    <td key={index}>
                      <Key
                        dangerouslySetInnerHTML={{
                          __html: technicalData.name,
                        }}
                      />
                    </td>
                    <td key={index}>
                      <span
                        dangerouslySetInnerHTML={{
                          __html: technicalData.value,
                        }}
                      />
                    </td>
                  </tr>
                )
            )}
          </TechnicalDataList>
        </InfoWrapper>
        <TechnicalAssets>
          <TechnicalImages>
            {prodTechnicalImg?.map((img, index) =>
              img ? (
                <TechnicalImage
                  key={index}
                  style={{ paddingTop: paddingTop?.current?.[index] }}
                >
                  <ImageWithFallback
                    isPrerenderRequest={isPrerenderRequest}
                    layout="fill"
                    objectFit="contain"
                    loading="eager"
                    src={getStrapiMedia(img || '')}
                    alt={`${translations?.pages?.productDetails?.productDetailsTitle}_${index}`}
                    fallbackSrc={DEFAULT_IMAGE}
                  />
                </TechnicalImage>
              ) : (
                <></>
              )
            )}
          </TechnicalImages>
          {isDownloadAvailable !== undefined && isDownloadAvailable === true ? (
            <ButtonWrapper>
              <StyledButton onClick={handleOpenModal}>
                <ButtonText>
                  {translations?.pages?.productDetails?.pdfDownload}
                </ButtonText>
              </StyledButton>
            </ButtonWrapper>
          ) : isDownloadAvailable === undefined ? (
            <ButtonWrapper>
              <StyledButton onClick={() => {}}>
                <ButtonText>{'...'}</ButtonText>
              </StyledButton>
            </ButtonWrapper>
          ) : (
            <></>
          )}
          {isModalOpen && (
            <DownloadManualModal
              open={isModalOpen}
              translations={translations}
              locale={locale}
              handleClose={handleCloseModal}
              sku={sku}
              userContext={userContext}
            />
          )}
        </TechnicalAssets>
      </StyledContentWrapper>
    ) : (
      <></>
    );
};

export default ProductDetailsFls;
